import { ApiClient } from "../api/client";
import { UserProvider } from "../api/user";
import { TranslationsProvider } from "../api/translations";
import { MetadataProvider } from "../fields/metadata";
import { CacheProvider } from "./cache";
import i18n from '@/i18n';
import { ConfigProvider } from "./config";
import { EventProvider } from "../event-bus";

export class Context {
    static ready = false
    static user: UserInfo
    static settings: SystemSettings
    static urls: UrlData
    static config = ConfigProvider

    private static _apiClient = new ApiClient()

    static async init() {
        if (this.ready) return;
        await Office.onReady();
        this.urls = await this.getUrlData(); // Needs to be before others to handle Unauthorized error
        await TranslationsProvider.init(i18n);
        [
            this.user,
            this.settings,
        ] = await Promise.all([
            this.getUserInfo(),
            this.getSystemSettings(),
            UserProvider.init(),
            MetadataProvider.init(),
        ]);
        EventProvider.init();
        this.ready = true;
    }

    static async getUserInfo() {
        return CacheProvider.getOrCreate<UserInfo>('context.user', () =>
            this._apiClient.get('/system/user'), 4);
    }

    static async getSystemSettings() {
        return CacheProvider.getOrCreate<SystemSettings>('context.settings', () =>
            this._apiClient.get('/system/settings'), 1);
    }

    static async getUrlData() {
        return CacheProvider.getOrCreate<UrlData>('context.urls', () =>
            this._apiClient.get('/system/urls', true), 0);
    }

    static reset() {
        CacheProvider.clear();
        this.ready = false;
    }
}

export interface UserInfo {
    userId: number
    systemId: number
    licenseType: LicenseType
    accessSettings: UserAccessSettings
}

export enum LicenseType {
    Unknown = -1,
    Standard,
    Plus,
    Enterprise,
    FreeTrial
}

export interface UserAccessSettings {
    accessLevel: number
    organisationEdit1stResponsible: boolean
    organisationEdit2ndResponsible: boolean
    activityEditResponsible: boolean
    activitySetCompleted: boolean
    opportunityEdit1stResponsible: boolean
    opportunityEdit2ndResponsible: boolean
    deliveryEdit1stResponsible: boolean
    deliverySetCompleted: boolean
    createOrganisation: boolean
    createEntity: boolean
    editAllEntities: boolean
    editOwnEntities: boolean
    editConnectedEntities: boolean
    supportEditResponsible: boolean
    supportEditOwner: boolean
    supportEditStatus: boolean
}

export interface SystemSettings {
    isB2C: boolean
    dateFormat: string
    timeFormat: string
    decimalSeparator: string
    thousandSeparator: string
    features: SystemFeatures
}

export interface SystemFeatures {
    calendarNew: boolean
    calendarOld: boolean
    activities: boolean
    opportunities: boolean
    deliveries: boolean
    supportCases: boolean
    products: boolean
    quotations: boolean
    historyLog: boolean
    currency: boolean
}

export interface UrlData {
    addinsUrl: string
    loginUrl: string
    filesUrl: string
    mainSystemUrl: string
}
